import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import DatePickerShortcuts from 'components/insights/filterBar/DatePickerShortcuts';
import DatePicker from 'components/shared/form/DatePicker';
import useIsMobile from 'hooks/useIsMobile';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

type Props = {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Prefix for the `data-testid`s: `<prefix>-range`, `<prefix>-start`, `<prefix>-end`. */
  testIdPrefix: string;
  /** Prefix for the pickers' `data-gtm-id`s (`<prefix>StartDateFilter` / `<prefix>EndDateFilter`); omit for none. */
  gtmIdPrefix?: string;
  /** Whether either end can be cleared to leave that side open. Default true. */
  clearable?: boolean;
  /** Latest date either picker accepts; the start is further capped by the chosen end. */
  maxDate?: Date;
};

// The inbound-referrals header's right-hand date range, laid out like InsightsDateRangePicker:
// two pickers, a dash, and the shortcut footer. Shared by the Tasks page (created range, either
// end clearable) and the referral History tab (Date Completed range, always a full range).
//
// Two-stage state: the pickers' selections only reach `onChange` (and so the query) when a
// calendar closes or a field blurs, not on every keystroke while someone types a date. The
// shortcut footer commits immediately — a preset is a complete choice.
export default function DateRangePicker({
  value,
  onChange,
  testIdPrefix,
  gtmIdPrefix,
  clearable = true,
  maxDate,
}: Props) {
  const isMobile = useIsMobile();
  const [draft, setDraftState] = useState<DateRange>(value);
  const draftRef = useRef(value);
  const setDraft = (next: DateRange) => {
    draftRef.current = next;
    setDraftState(next);
  };
  useEffect(() => setDraft(value), [value]);

  const commitDraft = () => onChange(draftRef.current);

  const DatePickerFooter = ({ dateInput }: { dateInput: any }) => (
    <DatePickerShortcuts
      startDate={draft.start}
      endDate={draft.end}
      datePickerRef={dateInput}
      setDates={([start, end]: Date[]) => {
        setDraft({ start, end });
        onChange({ start, end });
      }}
    />
  );

  return (
    <Container data-testid={`${testIdPrefix}-range`}>
      <DatePicker
        data-gtm-id={gtmIdPrefix && `${gtmIdPrefix}StartDateFilter`}
        data-testid={`${testIdPrefix}-start`}
        $fullWidth={isMobile}
        clearable={clearable}
        selected={draft.start}
        onChange={(val) => setDraft({ ...draftRef.current, start: (val as Date) ?? null })}
        onCalendarClose={commitDraft}
        onBlur={commitDraft}
        maxDate={draft.end ?? maxDate}
        renderFooter={DatePickerFooter}
        popperPlacement='bottom-start'
      />
      <Emdash>&mdash;</Emdash>
      <DatePicker
        data-gtm-id={gtmIdPrefix && `${gtmIdPrefix}EndDateFilter`}
        data-testid={`${testIdPrefix}-end`}
        $fullWidth={isMobile}
        clearable={clearable}
        selected={draft.end}
        onChange={(val) => setDraft({ ...draftRef.current, end: (val as Date) ?? null })}
        onCalendarClose={commitDraft}
        onBlur={commitDraft}
        minDate={draft.start ?? undefined}
        maxDate={maxDate}
        renderFooter={DatePickerFooter}
        popperPlacement='bottom-start'
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 160px;

    @media ${({ theme }) => theme.devices.desktop} {
      max-width: 140px;
    }
  }

  @media (max-width: 800px) {
    width: 100%;

    > div:first-child,
    > div:last-child {
      min-width: 0;
    }

    input {
      max-width: none;
    }
  }
`;

const Emdash = styled.div`
  margin: 0 8px;
`;
